import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Video, Mic, Users, Link as LinkIcon, Globe, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Eye, Send, Zap, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  { key: 'leadership_interview', message: 'Book a leadership interview — the single biggest score booster', impact: '+15', cta: 'Book Interview', link: 'mailto:brandstagevideo@gmail.com?subject=Leadership%20Interview%20Inquiry' },
  { key: 'podcast', message: 'Add your podcast appearances to showcase thought leadership', impact: '+10', cta: 'Add Podcast', action: 'scroll_to_videos' },
  { key: 'key_contact_video', message: 'Record a short video intro for your key contact', impact: '+8', cta: 'Add Video', action: 'scroll_to_key_contact' },
  { key: 'video_testimonials', message: 'Collect video testimonials from clients or partners', impact: '+7', cta: 'Request Testimonial', link: '/testimonials' },
  { key: 'linkedin', message: 'Add your LinkedIn company or personal page', impact: '+7', cta: 'Add LinkedIn', action: 'scroll_to_social' },
  { key: 'website', message: 'Link your company website to strengthen credibility', impact: '+5', cta: 'Add Website', action: 'scroll_to_social' },
  { key: 'quick_bites', message: 'Record Quick Bite videos to boost your activity score', impact: '+8', cta: 'Record Video', link: '/studio' },
  { key: 'youtube', message: 'Connect your YouTube channel', impact: '+5', cta: 'Add YouTube', action: 'scroll_to_social' },
];

const RATING_COLORS = {
  'Excellent': 'text-emerald-400',
  'Good': 'text-green-400',
  'Average': 'text-amber-400',
  'Needs Work': 'text-red-400'
};

const RATING_BG = {
  'Excellent': 'bg-emerald-500',
  'Good': 'bg-green-500',
  'Average': 'bg-amber-500',
  'Needs Work': 'bg-red-500'
};

export default function ProfileScoreWidget({ profileId, onSuggestionClick }) {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!profileId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/profiles/${profileId}/digital-score`, { withCredentials: true })
      .then(res => {
        setScoreData(res.data);
        setError(null);
      })
      .catch(err => {
        console.error('Score fetch error:', err);
        setError('Unable to load score');
      })
      .finally(() => setLoading(false));
  }, [profileId]);

  if (loading) {
    return (
      <div className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-4 w-full" data-testid="profile-score-widget">
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculating score…
        </div>
      </div>
    );
  }

  if (error || !scoreData) {
    return (
      <div className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-4 w-full" data-testid="profile-score-widget">
        <p className="text-zinc-500 text-sm">{error || 'Score unavailable'}</p>
      </div>
    );
  }

  const { total_score, rating, breakdown, benchmark, trend } = scoreData;
  const ratingColor = RATING_COLORS[rating] || 'text-zinc-400';
  const barColor = RATING_BG[rating] || 'bg-zinc-500';

  // Find top missing suggestions
  const missingSuggestions = SUGGESTIONS.filter(s => {
    const items = { ...breakdown.content.items, ...breakdown.presence.items, ...breakdown.activity.items };
    const item = items[s.key];
    return item && !item.has && item.earned === 0;
  }).slice(0, 2);

  return (
    <div className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-5 w-full" data-testid="profile-score-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-zinc-400 text-sm font-medium tracking-wide">Digital Readiness Score</span>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-zinc-500 hover:text-zinc-300 transition-colors"
          data-testid="score-expand-toggle"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Score + Rating */}
      <div className="flex items-end gap-3 mb-1">
        <span className={`text-5xl font-black tabular-nums ${ratingColor}`}>
          {total_score}
        </span>
        <div className="mb-1.5">
          <span className="text-zinc-600 text-sm">/100</span>
          <span className={`ml-2 text-xs font-bold uppercase tracking-wider ${ratingColor}`}>{rating}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-zinc-800 rounded-full h-2.5 mb-3 relative">
        <div className={`h-2.5 rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${total_score}%` }} />
        {/* Benchmark marker */}
        <div 
          className="absolute top-0 w-0.5 h-2.5 bg-white/40"
          style={{ left: `${benchmark.average}%` }}
          title={`Industry average: ${benchmark.average}%`}
        />
      </div>

      {/* Benchmark + Trend Row */}
      <div className="flex items-center justify-between text-xs mb-4">
        <span className="text-zinc-500">
          {benchmark.category} avg: <span className="text-zinc-400 font-medium">{benchmark.average}%</span>
          {total_score >= benchmark.average
            ? <span className="text-green-400 ml-1">({total_score - benchmark.average}% above)</span>
            : <span className="text-amber-400 ml-1">({benchmark.average - total_score}% below)</span>
          }
        </span>
        {trend.change !== null && (
          <span className={`flex items-center gap-0.5 font-medium ${trend.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend.change >= 0 ? '+' : ''}{trend.change} this month
          </span>
        )}
      </div>

      {/* Category Summary Bars */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'Content', score: breakdown.content.score, max: breakdown.content.max, icon: Video },
          { label: 'Presence', score: breakdown.presence.score, max: breakdown.presence.max, icon: Globe },
          { label: 'Activity', score: breakdown.activity.score, max: breakdown.activity.max, icon: Zap },
          { label: 'Profile', score: breakdown.completeness.score, max: breakdown.completeness.max, icon: FileText },
        ].map(cat => {
          const pct = cat.max > 0 ? Math.round((cat.score / cat.max) * 100) : 0;
          return (
            <div key={cat.label} className="text-center">
              <cat.icon className="w-3.5 h-3.5 text-zinc-500 mx-auto mb-1" />
              <div className="text-xs text-zinc-400 mb-1">{cat.label}</div>
              <div className="w-full bg-zinc-800 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full transition-all duration-500 ${pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{cat.score}/{cat.max}</div>
            </div>
          );
        })}
      </div>

      {/* Top Suggestions */}
      {missingSuggestions.length > 0 && (
        <div className="space-y-2 mb-3">
          {missingSuggestions.map(s => (
            <div key={s.key} className="bg-zinc-800/60 border border-zinc-700/50 rounded-lg p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <AlertCircle className="w-3.5 h-3.5 text-[#C41E3A] shrink-0" />
                <span className="text-zinc-300 text-xs truncate">{s.message}</span>
              </div>
              <span className="text-[#C41E3A] text-xs font-bold shrink-0">{s.impact}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expanded Breakdown */}
      {expanded && (
        <div className="border-t border-zinc-700 pt-4 mt-3 space-y-5" data-testid="score-breakdown">
          {/* Content Foundation */}
          <ScoreSection 
            title="Content Foundation" 
            weight="40"
            score={breakdown.content.score}
            max={breakdown.content.max}
            items={[
              { ...breakdown.content.items.leadership_interview, label: 'Leadership Interview', icon: Video },
              { ...breakdown.content.items.podcast, label: 'Podcast Appearance', icon: Mic },
              { ...breakdown.content.items.key_contact_video, label: 'Key Contact Video', icon: Video },
              { ...breakdown.content.items.video_testimonials, label: `Video Testimonials${breakdown.content.items.video_testimonials?.count ? ` (${breakdown.content.items.video_testimonials.count})` : ''}`, icon: Users },
            ]}
          />

          {/* Digital Presence */}
          <ScoreSection 
            title="Digital Presence" 
            weight="25"
            score={breakdown.presence.score}
            max={breakdown.presence.max}
            items={[
              { ...breakdown.presence.items.linkedin, label: 'LinkedIn', icon: LinkIcon },
              { ...breakdown.presence.items.website, label: 'Website', icon: Globe },
              { ...breakdown.presence.items.youtube, label: 'YouTube', icon: LinkIcon },
              { ...breakdown.presence.items.twitter, label: 'X (Twitter)', icon: LinkIcon },
              { ...breakdown.presence.items.instagram, label: 'Instagram', icon: LinkIcon },
            ]}
          />

          {/* Activity & Engagement */}
          <ScoreSection 
            title="Activity & Engagement" 
            weight="25"
            score={breakdown.activity.score}
            max={breakdown.activity.max}
            items={[
              { ...breakdown.activity.items.quick_bites, label: `Quick Bites (${breakdown.activity.items.quick_bites?.count || 0} this month)`, icon: Video, tiered: true },
              { ...breakdown.activity.items.profile_views, label: `Profile Views (${breakdown.activity.items.profile_views?.count || 0} this month)`, icon: Eye, tiered: true },
              { ...breakdown.activity.items.engagement_received, label: `RSVPs & Interest (${breakdown.activity.items.engagement_received?.count || 0})`, icon: Users, tiered: true },
              { ...breakdown.activity.items.outreach, label: `Invitations & Opportunities (${breakdown.activity.items.outreach?.count || 0})`, icon: Send, tiered: true },
            ]}
          />

          {/* Profile Completeness */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-zinc-500 text-xs uppercase tracking-wide">Profile Completeness</p>
              <span className="text-xs text-zinc-400">{breakdown.completeness.score}/{breakdown.completeness.max}</span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full ${breakdown.completeness.score >= 8 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${(breakdown.completeness.score / breakdown.completeness.max) * 100}%` }} />
            </div>
          </div>

          {/* Benchmark Context */}
          <div className="bg-zinc-800/40 rounded-lg p-3">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">Industry Benchmark — {benchmark.category}</p>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-zinc-500">Avg <span className="text-zinc-300 font-medium">{benchmark.average}%</span></span>
              <span className="text-zinc-500">Good <span className="text-green-400 font-medium">{benchmark.good}%</span></span>
              <span className="text-zinc-500">Excellent <span className="text-emerald-400 font-medium">{benchmark.excellent}%</span></span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      {!expanded && (
        <button 
          onClick={() => setExpanded(true)}
          className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
        >
          See full breakdown →
        </button>
      )}
    </div>
  );
}

function ScoreSection({ title, weight, score, max, items }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <p className="text-zinc-500 text-xs uppercase tracking-wide">{title} <span className="text-zinc-600">({weight}%)</span></p>
        <span className="text-xs text-zinc-400">{score}/{max}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => {
          const filled = item.earned > 0;
          const partial = item.tiered && item.earned > 0 && item.earned < item.max;
          return (
            <div key={i} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-1.5">
                {filled ? (
                  <CheckCircle className={`w-3.5 h-3.5 ${partial ? 'text-amber-400' : 'text-green-400'}`} />
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full border border-zinc-600 inline-block" />
                )}
                <span className={filled ? 'text-zinc-300' : 'text-zinc-500'}>{item.label}</span>
              </div>
              <span className={filled ? (partial ? 'text-amber-400' : 'text-green-400') : 'text-zinc-600'}>
                {filled ? `+${item.earned}` : `0/${item.max}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
